import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { SiInstagram, SiLinkedin } from 'react-icons/si';
import { SOCIAL_LINKS } from '@/lib/socials';

const FORMSUBMIT_ENDPOINT =
  'https://formsubmit.co/ajax/ks.score.sound@gmail.com';

// Reel Crafter icon
function ReelCrafterIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18 3H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3ZM8 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm4-4a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm6 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0-10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
    </svg>
  );
}

const CONTACT_LINKS = [
  {
    label: 'Email',
    value: 'ks.score.sound@gmail.com',
    href: SOCIAL_LINKS.email,
    icon: <Mail className="w-5 h-5" />,
    description: 'Fastest way to reach me',
    accent: 'var(--primary)',
    external: false,
  },
  {
    label: 'Instagram',
    value: '@_solopov_132',
    href: SOCIAL_LINKS.instagram,
    icon: <SiInstagram className="w-5 h-5" />,
    description: 'Behind-the-scenes & updates',
    accent: '#E1306C',
    external: true,
  },
  {
    label: 'LinkedIn',
    value: 'Konstantin Solopov',
    href: SOCIAL_LINKS.linkedin,
    icon: <SiLinkedin className="w-5 h-5" />,
    description: 'Professional profile',
    accent: '#0A66C2',
    external: true,
  },
  {
    label: 'Reel Crafter',
    value: 'Listen to my reel',
    href: SOCIAL_LINKS.reelcrafter,
    icon: <ReelCrafterIcon />,
    description: 'Full scoring demo reel',
    accent: 'var(--accent)',
    external: true,
  },
];

interface FormState {
  name: string;
  email: string;
  project: string;
  message: string;
}

const initialState: FormState = { name: '', email: '', project: '', message: '' };

type Status = 'idle' | 'sending' | 'success' | 'error';

export function ContactSection() {
  const [form, setForm]     = useState<FormState>(initialState);
  const [status, setStatus] = useState<Status>('idle');
  const [errMsg, setErrMsg] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');
    try {
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          'Project Type': form.project || 'Not specified',
          message: form.message,
          // Formsubmit.co config keys
          _subject: `[K.S. Score & Sound] New inquiry from ${form.name}`,
          _captcha: 'false',
          _template: 'table',
        }),
      });
      const data = await res.json();
      if (data.success === 'true' || data.success === true) {
        setStatus('success');
        setForm(initialState);
      } else {
        throw new Error(data.message ?? 'Submission failed');
      }
    } catch (err: unknown) {
      setStatus('error');
      setErrMsg(err instanceof Error ? err.message : 'Something went wrong. Please try emailing directly.');
    }
  };

  return (
    <section id="contact" className="py-24 px-6 relative">
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px"
        style={{ background: 'linear-gradient(to right, transparent, var(--chart-4), transparent)' }}
      />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p className="text-xs font-mono text-chart-4 uppercase tracking-widest mb-3">
            Get in Touch
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Start a Project
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Have a film, game, or project that needs an original score? Let's talk — or explore
            my work on social before you reach out.
          </p>
        </motion.div>

        {/* Social / contact link cards */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
        >
          {CONTACT_LINKS.map((link, i) => (
            <motion.a
              key={link.label}
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              whileHover={{ y: -4, scale: 1.02 }}
              className="group rounded-2xl border border-border bg-card/50 backdrop-blur-sm p-5 flex flex-col gap-3 cursor-pointer transition-shadow duration-300"
              style={{
                borderTopColor: link.accent,
                borderTopWidth: '2px',
                boxShadow: `0 4px 20px -6px ${link.accent}22`,
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.boxShadow =
                  `0 8px 32px -8px ${link.accent}55`;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.boxShadow =
                  `0 4px 20px -6px ${link.accent}22`;
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                style={{ background: `${link.accent}22`, color: link.accent }}
              >
                {link.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 mb-0.5">
                  <p className="text-xs font-mono uppercase tracking-widest font-semibold" style={{ color: link.accent }}>
                    {link.label}
                  </p>
                  {link.external && <ExternalLink className="w-3 h-3 opacity-40" style={{ color: link.accent }} />}
                </div>
                <p className="text-sm font-semibold text-foreground truncate">{link.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{link.description}</p>
              </div>
            </motion.a>
          ))}
        </motion.div>

        {/* Form + info row */}
        <div className="grid md:grid-cols-5 gap-10 items-start">
          {/* Contact info panel */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-2 space-y-6"
          >
            {/* Studio card */}
            <div
              className="rounded-2xl border border-border bg-card/60 p-6"
              style={{ boxShadow: '0 20px 60px -20px oklch(0.65 0.18 180 / 0.15)' }}
            >
              <img
                src="/images/logo.png"
                alt="K.S. Score & Sound"
                className="w-16 h-16 object-contain mb-3"
              />
              <p className="text-base font-bold text-foreground mb-1">K.S. Score & Sound</p>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                Professional film & media scoring studio. Cinematic music that serves your story.
              </p>
              <div className="space-y-2 text-xs text-muted-foreground">
                <p className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
                  Based in Boston, MA
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block" />
                  Available for remote projects worldwide
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--chart-3)' }} />
                  Graduating Berklee — Spring 2026
                </p>
              </div>
            </div>

            {/* Response time note */}
            <div className="rounded-xl border border-border bg-card/40 px-5 py-4 flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0 animate-pulse" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Typical response time is <strong className="text-foreground">24–48 hours</strong>.
                For urgent projects, mention your deadline in the message.
              </p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-3"
          >
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-border bg-card/60 p-10 text-center"
              >
                <CheckCircle className="w-14 h-14 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">Message Sent!</h3>
                <p className="text-muted-foreground text-sm">
                  Thank you for reaching out — I'll get back to you within 24–48 hours.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition"
                >
                  Send Another
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-2xl border border-border bg-card/60 backdrop-blur-sm p-6 md:p-8 space-y-5"
                style={{ boxShadow: '0 20px 60px -15px oklch(0.68 0.22 25 / 0.12)' }}
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background/50 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background/50 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                    Project Type
                  </label>
                  <select
                    name="project"
                    value={form.project}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background/50 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition appearance-none"
                  >
                    <option value="">Select a project type…</option>
                    <option value="Film Score">Film Score</option>
                    <option value="Game Music">Game Music</option>
                    <option value="Sound Design">Sound Design</option>
                    <option value="Custom Arrangement">Custom Arrangement</option>
                    <option value="Orchestration">Orchestration</option>
                    <option value="Music Editing">Music Editing</option>
                    <option value="Music Supervision">Music Supervision</option>
                    <option value="Score Preparation">Score Preparation</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                    Tell me about your project
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Brief description, timeline, budget, references…"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background/50 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition resize-none"
                  />
                </div>

                {/* Error message */}
                {status === 'error' && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3"
                  >
                    ⚠️ {errMsg || 'Something went wrong.'}{' '}
                    <a href={SOCIAL_LINKS.email} className="underline hover:text-red-300">
                      Email directly
                    </a>
                    .
                  </motion.p>
                )}

                <motion.button
                  type="submit"
                  disabled={status === 'sending'}
                  whileTap={{ scale: status === 'sending' ? 1 : 0.97 }}
                  whileHover={{ opacity: status === 'sending' ? 1 : 0.92 }}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                    color: 'var(--primary-foreground)',
                  }}
                >
                  {status === 'sending' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </motion.button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
